//@ts-ignore
import React from 'react';
import { Component, RefObject, createContext, createRef } from 'react';
import { FullScreenProps, FullScreenPropsAndChildren, FullScreenState } from './types';

interface IFullScreenContext {
  isFullScreen: boolean;
  toggleFullscreen: any;
}
const FullScreenContext = createContext({} as IFullScreenContext);

class FullScreenStateProvider extends Component<
  FullScreenProps & { children?: any },
  FullScreenState
> {
  targetRef: RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.targetRef = createRef();
    this.state = {
      isFullScreen: false,
      // eslint-disable-next-line react/no-unused-state
      toggleFullscreen: this.toggleFullscreen.bind(this),
    };
  }

  componentDidMount() {
    const targetElem = this.getTargetElement();
    if (targetElem)
      targetElem.onfullscreenchange = (event) => {
        const isTargetElem = targetElem === event.target;
        if (!isTargetElem) {
          // Need to sync state when fullscreen mode turned off but isFullScreen is true
          // Case when more than one fullscreen mode is active and user closed mode by ESC tab
          if (this.state.isFullScreen === true && !document.fullscreenElement) {
            this.setState({ isFullScreen: false });
          }
          return;
        }

        const isFullScreen = document.fullscreenElement === event.target;
        this.setState({ isFullScreen });
      };
    if (this.props.startFullscreen) {
      this.toggleFullscreen();
    }
  }

  componentWillUnmount() {
    const targetElem = this.getTargetElement();
    if (targetElem) targetElem.onfullscreenchange = null;
  }

  toggleFullscreen() {
    if (!this.state.isFullScreen) {
      this.openFullscreen()?.catch(console.error);
    } else {
      this.exitFullscreen()?.catch(console.error);
    }
    // noinspection TypeScriptValidateTypes
    this.setState({ isFullScreen: !this.state.isFullScreen });
  }

  exitFullscreen(): Promise<any> {
    if (!document.fullscreen) return Promise.resolve();
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    //@ts-ignore
    if (document.webkitExitFullscreen) {
      //@ts-ignore
      return document.webkitExitFullscreen();
    }
    //@ts-ignore
    if (document.msExitFullscreen) {
      //@ts-ignore
      return document.msExitFullscreen();
    }
    return Promise.reject('API does not response');
  }

  openFullscreen(): Promise<any> {
    return this.exitFullscreen().then(() => {
      const elem = this.getTargetElement();
      if (elem) {
        if (elem.requestFullscreen) {
          return elem.requestFullscreen();
        }
        //@ts-ignore
        if (elem.webkitRequestFullscreen) {
          //@ts-ignore
          return elem.webkitRequestFullscreen();
        }
        //@ts-ignore
        if (elem.msRequestFullscreen) {
          //@ts-ignore
          return elem.msRequestFullscreen();
        }
      }
      return Promise.reject('API does not response');
    });
  }

  getTargetElement() {
    const { rootBody } = this.props;
    return rootBody ? window.document.body : this.targetRef.current;
  }

  render() {
    const { children, attrs } = this.props;
    const attr = { id: attrs?.id, className: attrs?.class };
    const customStyles = attrs?.styles || {};
    const styles = {
      height: '100%',
      width: '100%',
      background: '#fff',
      ...customStyles,
    };
    return (
      <FullScreenContext.Provider
        value={
          this.state
            ? {
                isFullScreen: this.state.isFullScreen,
                toggleFullscreen: this.state.toggleFullscreen ?? (() => null),
              }
            : {
                isFullScreen: true,
                toggleFullscreen: () => null,
              }
        }
      >
        <div ref={this.targetRef} {...attr} style={styles}>
          {children}
        </div>
      </FullScreenContext.Provider>
    );
  }
}

export default function FullScreen({ children, ...rest }: FullScreenPropsAndChildren) {
  return (
    /*@ts-ignore*/
    <FullScreenStateProvider {...rest}>
      <FullScreenContext.Consumer>{children}</FullScreenContext.Consumer>
    </FullScreenStateProvider>
  );
}
